import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { newPasswordSchema, MIN_PASSWORD_LENGTH } from '@shared/schemas/auth';
import styles from './AuthForm.module.css';

/** What GET /auth/invite/:token returns for a live link. */
interface InviteDetails {
  email: string;
  displayName: string;
  organizationName: string;
}

/**
 * Redeeming an invitation: the screen where a team member chooses their first
 * password.
 *
 * The account already exists — an owner created it on the Team page — so
 * nothing here creates a user, picks a role, or names an organization. All of
 * that is fixed by the row the token points at, and the only thing this form
 * sends is a password.
 *
 * The link is checked BEFORE the form is offered, so an expired or already-used
 * link says so immediately rather than after someone has typed a password twice.
 */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const { token = '' } = useParams<{ token: string }>();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  /** Set when the LINK is unusable — a terminal state, so no form is shown. */
  const [linkError, setLinkError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * A ref, not the isSubmitting state. React batches state updates, so two
   * clicks in the same tick both observe isSubmitting === false and both
   * submit; the second would then be told the invitation was "already used",
   * having been beaten by the user's own first click.
   */
  const submitGuard = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const details = await apiGet<InviteDetails>(`/auth/invite/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setInvite(details);
        }
      } catch (err) {
        if (!cancelled) {
          setLinkError(
            err instanceof ApiClientError ? err.message : 'That invitation link is not valid.'
          );
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitGuard.current) return;

    const parsed = newPasswordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Choose a password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Those passwords do not match.');
      return;
    }

    submitGuard.current = true;
    setIsSubmitting(true);
    setError('');

    try {
      await apiPost(`/auth/invite/${encodeURIComponent(token)}`, { password });
      await refresh();
      navigate('/today');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Could not set your password. Try again.');
      }
      // Only released on failure. On success the component navigates away, and
      // re-enabling it would allow a second redemption of a link that is now
      // spent.
      submitGuard.current = false;
      setIsSubmitting(false);
    }
  }

  if (isChecking) {
    return (
      <main className={styles.main} id="main">
        <div className={styles.card}>
          <h1 className={styles.title}>Ball &amp; Chaney Honey Do</h1>
          <p className={styles.status}>Checking your invitation…</p>
        </div>
      </main>
    );
  }

  if (linkError || !invite) {
    return (
      <main className={styles.main} id="main">
        <div className={styles.card}>
          <h1 className={styles.title}>Invitation problem</h1>
          <p className={styles.error} role="alert">
            {linkError || 'That invitation link is not valid.'}
          </p>
          <p className={styles.footer}>
            <Link to="/login" className={styles.footerLink}>
              Go to sign in
            </Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main} id="main">
      <div className={styles.card}>
        <h1 className={styles.title}>Welcome, {invite.displayName}</h1>
        <p className={styles.subtitle}>
          Choose a password to finish joining {invite.organizationName}.
        </p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="invite-email" className={styles.label}>
              Email
            </label>
            {/*
              Shown, but not editable. Which account the link is for is decided
              by the token, so an editable field here would be a lie: typing a
              different address would change nothing about what gets set.
            */}
            <p id="invite-email" className={styles.readonlyValue}>
              {invite.email}
            </p>
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={error ? 'invite-error' : undefined}
            />
            <p className={styles.hint}>At least {MIN_PASSWORD_LENGTH} characters.</p>
          </div>

          <div className={styles.field}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={error ? 'invite-error' : undefined}
            />
          </div>

          {error && (
            <p id="invite-error" className={styles.error} role="alert" aria-live="polite">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Setting password…' : 'Set password and sign in'}
          </button>
        </form>
      </div>
    </main>
  );
}
